import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import Header from './components/HeaderNew';
import OnboardingPage from './pages/Onboarding';
import SettingsPage from './pages/SettingsPage';
import DashboardNew from './pages/DashboardNew';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import LandingIntroPage from './pages/LandingIntroPage';
import AboutForzaPage from './pages/AboutForzaPage';
import HealthSourcesPage from './pages/HealthSourcesPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocaleProvider } from './context/LocaleContext';
import { TimerProvider } from './context/TimerContext';
import LeaderboardPage from './pages/LeaderboardPage';
import CalendarPage from './pages/CalendarPage';
import ShopPage from './pages/ShopPage';
import AdminImageReviewPage from './pages/AdminImageReviewPage';
import AdminEarlyAccessPage from './pages/AdminEarlyAccessPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import SubscriptionPage from './pages/SubscriptionPage';
// Import the new legal pages
import TermsOfService from './pages/Legal/TermsOfService';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import SupportPage from './pages/Legal/Support';
import EmailVerificationPage from './pages/EmailVerificationPage';
import { CoachUsageProvider } from './context/CoachUsageContext';
import { SSEProvider } from './context/SSEContext'; // Import SSEProvider
import { ToastProvider } from './context/ToastContext';
import { DashboardRefreshProvider, useDashboardRefresh } from './context/DashboardRefreshContext';
import { NativeCapsProvider } from './context/NativeCapsContext';
import AiDataProcessingConsentGate from './components/AiDataProcessingConsentGate';
import { deleteAccount } from './api/dataService';
import { recordAiDataProcessingConsent } from './api/authService';
import { AI_DATA_PROCESSING_CONSENT_VERSION, LOGIN_NOTICE_STORAGE_KEY, requiresAiDataProcessingConsent } from './utils/aiDataProcessingConsent';
import { useLocale } from './context/LocaleContext';


export const getOnboardingRouteState = ({ user, loading }) => {
  if (loading) {
    return { status: 'loading' };
  }
  if (!user) {
    return { status: 'redirect', path: '/login' };
  }
  if (user.onboardingComplete) {
    return { status: 'redirect', path: '/dashboard' };
  }
  return { status: 'render' };
};

export const normalizeRecoveryDaysCompleted = (value) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, Math.min(parsed, 3));
};

export const PUBLIC_REGISTRATION_REDIRECT_PATHS = [
  '/early-access',
  '/early-access/unlock',
];

export const getRecoveryShellState = ({ user, loading, pathname }) => {
  const isRecoveryMode = Boolean(user?.isRecoveryMode);
  const recoveryDaysCompleted = normalizeRecoveryDaysCompleted(user?.recoveryDaysCompleted);

  return {
    isRecoveryMode,
    recoveryDaysCompleted,
    shouldShowRecoveryBanner: false,
  };
};

const AUTH_SHELL_BACKGROUND = 'linear-gradient(135deg, #0B0F19 0%, #111827 50%, #0A192F 100%)';

export const getAppShellBackground = ({ pathname, isRecoveryMode }) => {
  if (isRecoveryMode) {
    return {
      className: 'recovery-bg',
      style: undefined,
    };
  }

  if (pathname === '/login' || pathname === '/register' || pathname === '/early-access/unlock') {
    return {
      className: '',
      style: {
        backgroundColor: '#0B0F19',
        backgroundImage: AUTH_SHELL_BACKGROUND,
      },
    };
  }

  if (pathname === '/about') {
    return {
      className: 'bg-[#0d0d0d]',
      style: {
        backgroundColor: '#0d0d0d',
      },
    };
  }

  return {
    className: 'bg-[#0f1115]',
    style: {
      backgroundColor: '#0f1115',
    },
  };
};

export const getShouldHideHeader = ({ pathname, user }) => {
  const normalizedPathname = pathname.replace(/\/$/, '') || '/';

  if (normalizedPathname === '/' || normalizedPathname.startsWith('/admin/')) {
    return true;
  }

  if (normalizedPathname === '/about') {
    return !user;
  }

  return false;
};

const NativeHealthKitSyncBridge = () => {
  const { setUser } = useAuth();
  const { refresh } = useDashboardRefresh();

  useEffect(() => {
    const handleNativeEvent = (event) => {
      const payload = event?.detail;
      if (!payload || payload.type !== 'FORZA_HEALTHKIT_SYNCED') {
        return;
      }
      if (payload.user) {
        setUser(payload.user);
      }
      refresh();
    };

    window.addEventListener('forza:native', handleNativeEvent);
    return () => window.removeEventListener('forza:native', handleNativeEvent);
  }, [refresh, setUser]);

  return null;
};

const NativeSubscriptionBridge = () => {
  const { updateUser } = useAuth();

  useEffect(() => {
    const handleNativeEvent = (event) => {
      const payload = event?.detail;
      if (!payload || payload.type !== 'FORZA_SUBSCRIPTION_RESULT' || !payload.user) {
        return;
      }
      updateUser(payload.user);
    };

    window.addEventListener('forza:native', handleNativeEvent);
    return () => window.removeEventListener('forza:native', handleNativeEvent);
  }, [updateUser]);

  return null;
};

const NativePushNotificationsBridge = () => {
  const { updateUser } = useAuth();

  useEffect(() => {
    const handleNativeEvent = (event) => {
      const payload = event?.detail;
      if (!payload || payload.type !== 'FORZA_PUSH_NOTIFICATIONS_RESULT') {
        return;
      }

      if (payload.currentInstallPushNotificationStatus) {
        window.__FORZA_NATIVE_PUSH_STATUS__ = payload.currentInstallPushNotificationStatus;
      }

      if (payload.user) {
        updateUser(payload.user);
      }
    };

    window.addEventListener('forza:native', handleNativeEvent);
    return () => window.removeEventListener('forza:native', handleNativeEvent);
  }, [updateUser]);

  return null;
};

export const OnboardingRoute = () => {
  const { user, loading } = useAuth();
  const { status, path } = getOnboardingRouteState({ user, loading });

  if (status === 'loading') {
    return null;
  }

  if (status === 'redirect') {
    return <Navigate to={path} replace />;
  }

  return <OnboardingPage />;
};

const AppContent = () => {
  const { user, loading, updateUser, logout } = useAuth(); // Get loading state
  const { t } = useLocale();
  const [consentError, setConsentError] = React.useState('');
  const [isSavingConsent, setIsSavingConsent] = React.useState(false);
  const [isDeletingForConsent, setIsDeletingForConsent] = React.useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const consentRequired = requiresAiDataProcessingConsent(user);
  const shouldHideHeader = getShouldHideHeader({ pathname: location.pathname, user });
  const {
    isRecoveryMode,
  } = getRecoveryShellState({ user, loading, pathname: location.pathname });
  const shellBackground = getAppShellBackground({
    pathname: location.pathname,
    isRecoveryMode,
  });

  useEffect(() => {
    if (consentRequired) {
      return;
    }
    if (!loading && user && user.onboardingComplete && location.pathname === '/onboarding') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, location, navigate, consentRequired]);

  useEffect(() => {
    if (consentRequired) {
      return;
    }
    // This effect now runs whenever the user object changes after loading is complete.
    if (!loading && user && user.onboardingComplete === false && location.pathname !== '/onboarding') {
      const allowedPaths = ['/onboarding', '/terms-of-service', '/privacy-policy', '/subscription', '/verify-email'];
      if (!allowedPaths.includes(location.pathname)) {
        navigate('/onboarding', { replace: true });
      }
    }
  }, [user, loading, location, navigate, consentRequired]);

  // Display a loading spinner or placeholder while the session is being checked.
  if (loading) {
    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <h2>{t('appShell.loading', 'Loading...')}</h2>
        </div>
    );
  }

  if (user && consentRequired) {
    return (
      <AiDataProcessingConsentGate
        error={consentError}
        isSubmitting={isSavingConsent}
        isDeleting={isDeletingForConsent}
        onAgree={async () => {
          setConsentError('');
          setIsSavingConsent(true);
          try {
            const response = await recordAiDataProcessingConsent(
              user.requiredAiDataProcessingConsentVersion || AI_DATA_PROCESSING_CONSENT_VERSION
            );
            if (response?.user) {
              updateUser(response.user);
            }
          } catch (error) {
            setConsentError(error?.response?.data?.message || error?.message || t('consent.saveError', 'Failed to save AI data processing consent.'));
          } finally {
            setIsSavingConsent(false);
          }
        }}
        onDecline={async () => {
          setConsentError('');
          setIsDeletingForConsent(true);
          try {
            await deleteAccount();
            sessionStorage.setItem(
              LOGIN_NOTICE_STORAGE_KEY,
              t('consent.deletedNotice', 'Your account was deleted because AI data processing consent was declined.')
            );
            logout();
          } catch (error) {
            setConsentError(error?.response?.data?.message || t('consent.deleteError', 'Failed to delete account.'));
          } finally {
            setIsDeletingForConsent(false);
          }
        }}
      />
    );
  }


  return (
    <DashboardRefreshProvider>
      <NativeHealthKitSyncBridge />
      <NativeSubscriptionBridge />
      <NativePushNotificationsBridge />
      <div className={`min-h-screen ${shellBackground.className}`.trim()} style={shellBackground.style}>
        {!shouldHideHeader && <Header />}
        <CoachUsageProvider>
          <div
            className="pb-28 sm:pb-32"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
          >
            <Routes>
              <Route path="/" element={<LandingIntroPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {PUBLIC_REGISTRATION_REDIRECT_PATHS.map((path) => (
                <Route key={path} path={path} element={<Navigate to="/register" replace />} />
              ))}
              <Route path="/about" element={<AboutForzaPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/auth/callback" element={<GoogleCallbackPage />} />
              <Route path="/onboarding" element={<OnboardingRoute />} />
              {/* Legal Pages */}
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/support"
                element={
                  <PrivateRoute>
                    <SupportPage />
                  </PrivateRoute>
                }
              />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardNew />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/health-sources"
                element={
                  <PrivateRoute>
                    <HealthSourcesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/Calendar"
                element={
                  <PrivateRoute>
                    <CalendarPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <PrivateRoute>
                    <LeaderboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/shop"
                element={
                  <PrivateRoute>
                    <ShopPage />
                  </PrivateRoute>
                }
              />
              <Route path="/admin/image-review" element={<AdminImageReviewPage />} />
              <Route path="/admin/early-access" element={<AdminEarlyAccessPage />} />
              <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            </Routes>
          </div>
        </CoachUsageProvider>
      </div>
    </DashboardRefreshProvider>
  );
}

const TimerWrapper = () => {
  const { user } = useAuth();
  return (
    <TimerProvider user={user}>
      <AppContent />
    </TimerProvider>
  )
}


function App() {
  return (
    <Router>
      <AuthProvider>
        <LocaleProvider>
          <ToastProvider>
            <SSEProvider>
              <NativeCapsProvider>
                <TimerWrapper />
              </NativeCapsProvider>
            </SSEProvider>
          </ToastProvider>
        </LocaleProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
